import { Button } from "@/components/ui/button";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from "react";
import { INGREDIENT_STORIES, type IngredientStoryProduct } from "./ingredientStoryContent";
import "./IngredientScrollStory.css";

export interface IngredientScrollStoryProps {
  product: IngredientStoryProduct;
  stickyTop?: number;
}

function ArrowIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 12h15M13 5l7 7-7 7" /></svg>;
}

/** Ingredient-only sibling of the live Mag story. No commerce, mock data or CTA. */
export default function IngredientScrollStory({ product, stickyTop = 144 }: IngredientScrollStoryProps) {
  const story = INGREDIENT_STORIES[product];
  const forms = story.ingredients;
  const uid = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const artRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeRef = useRef(0);
  const jumpRef = useRef<((index: number) => void) | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [horizontal, setHorizontal] = useState(false);
  const safeStickyTop = Number.isFinite(stickyTop) ? Math.max(0, stickyTop) : 144;
  const activeForm = forms[activeIndex];
  const panelId = `${uid}-ingredient-panel`;
  const captionId = `${uid}-ingredient-caption`;
  const tabId = (index: number) => `${uid}-ingredient-${index}`;
  const rootStyle = { "--ingredient-story-sticky-top": `${safeStickyTop}px` } as CSSProperties;

  const selectForm = useCallback((index: number, focus = false) => {
    if (index < 0 || index >= forms.length) return;
    activeRef.current = index;
    setActiveIndex(index);
    if (focus) tabRefs.current[index]?.focus({ preventScroll: true });
    jumpRef.current?.(index);
  }, [forms.length]);

  const onTabKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number | undefined;
    if ((horizontal && event.key === "ArrowRight") || (!horizontal && event.key === "ArrowDown")) next = (index + 1) % forms.length;
    if ((horizontal && event.key === "ArrowLeft") || (!horizontal && event.key === "ArrowUp")) next = (index + forms.length - 1) % forms.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = forms.length - 1;
    if (next !== undefined) {
      event.preventDefault();
      event.stopPropagation();
      selectForm(next, true);
    }
  };

  useEffect(() => {
    const root = rootRef.current;
    const track = trackRef.current;
    const stage = stageRef.current;
    const art = artRef.current;
    if (!root || !track || !stage || !art) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mobile = window.matchMedia("(max-width: 760px)");
    let frame = 0;
    let disposed = false;
    let needsMeasure = true;
    let stageHeight = 0;
    let viewportHeight = window.innerHeight;
    let pinEnabled = true;
    let pendingJump: number | null = null;
    let jumpTarget: number | null = null;
    let jumpExpires = 0;
    let lastArtOffset = "";
    const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

    const schedule = () => {
      if (!disposed && !frame) frame = window.requestAnimationFrame(update);
    };
    const resize = () => { needsMeasure = true; schedule(); };
    const cancelJump = () => {
      if (jumpTarget !== null || pendingJump !== null) { pendingJump = null; jumpTarget = null; schedule(); }
    };
    const cancelJumpFromKeyboard = (event: KeyboardEvent) => {
      if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)) cancelJump();
    };

    const update = () => {
      frame = 0;
      if (disposed) return;
      if (needsMeasure) {
        viewportHeight = window.innerHeight;
        setHorizontal(mobile.matches);
        // Keep the image and controls readable in short windows and landscape.
        const bottomSpace = mobile.matches ? 96 : 28;
        pinEnabled = !reducedMotion.matches && viewportHeight - safeStickyTop - bottomSpace >= (mobile.matches ? 430 : 470);
        root.dataset.scrollEnabled = String(pinEnabled);
        stageHeight = stage.getBoundingClientRect().height;
        needsMeasure = false;
      }
      const bounds = track.getBoundingClientRect();
      const travel = Math.max(0, bounds.height - stageHeight);
      const progress = travel > 1 ? clamp((safeStickyTop - bounds.top) / travel, 0, 1) : 0;
      const artOffset = reducedMotion.matches || !pinEnabled ? "0px" : `${((progress - 0.5) * -12).toFixed(2)}px`;
      if (artOffset !== lastArtOffset) {
        art.style.setProperty("--ingredient-story-art-y", artOffset);
        lastArtOffset = artOffset;
      }

      if (!pinEnabled || travel <= 1) {
        pendingJump = null;
        jumpTarget = null;
        // Native document flow retains the manually selected form.
        return;
      }
      if (pendingJump !== null) {
        const index = pendingJump;
        pendingJump = null;
        const start = window.scrollY + bounds.top - safeStickyTop;
        const maximumScroll = Math.max(0, document.documentElement.scrollHeight - viewportHeight);
        const destination = clamp(start + travel * (index + 0.5) / forms.length, 0, maximumScroll);
        jumpTarget = index;
        jumpExpires = performance.now() + 2200;
        // Native instant positioning avoids competing with desktop smooth-scroll ownership.
        window.scrollTo({ top: destination, behavior: "instant" });
        if (Math.abs(window.scrollY - destination) < 1) { jumpTarget = null; schedule(); }
        return;
      }
      const index = Math.min(forms.length - 1, Math.floor(progress * forms.length));
      if (jumpTarget !== null) {
        if (index !== jumpTarget && performance.now() < jumpExpires) return;
        jumpTarget = null;
      }
      if (index !== activeRef.current) {
        activeRef.current = index;
        setActiveIndex(index);
      }
    };

    jumpRef.current = (index) => { pendingJump = index; schedule(); };
    const observer = new ResizeObserver(resize);
    observer.observe(track);
    observer.observe(stage);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", resize, { passive: true });
    window.addEventListener("wheel", cancelJump, { passive: true });
    window.addEventListener("touchstart", cancelJump, { passive: true });
    window.addEventListener("pointerdown", cancelJump, { passive: true });
    window.addEventListener("keydown", cancelJumpFromKeyboard);
    window.addEventListener("scrollend", cancelJump, { passive: true });
    window.visualViewport?.addEventListener("resize", resize, { passive: true });
    reducedMotion.addEventListener("change", resize);
    mobile.addEventListener("change", resize);
    void document.fonts?.ready.then(() => { if (!disposed) resize(); });
    schedule();

    return () => {
      disposed = true;
      jumpRef.current = null;
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", resize);
      window.removeEventListener("wheel", cancelJump);
      window.removeEventListener("touchstart", cancelJump);
      window.removeEventListener("pointerdown", cancelJump);
      window.removeEventListener("keydown", cancelJumpFromKeyboard);
      window.removeEventListener("scrollend", cancelJump);
      window.visualViewport?.removeEventListener("resize", resize);
      reducedMotion.removeEventListener("change", resize);
      mobile.removeEventListener("change", resize);
    };
  }, [safeStickyTop, forms.length]);

  return (
    <div ref={rootRef} className="ingredient-story" data-scroll-enabled="false" data-ingredient-product={product} style={rootStyle}>
      <section id={`${product}-ingredient-story`} className="ingredient-story__ingredients" aria-labelledby={`${uid}-ingredients-heading`}>
        <div className="ingredient-story__wrap">
          <div className="ingredient-story__editorial-head">
            <div>
              <p className="ingredient-story__eyebrow">{story.eyebrow}</p>
              <h2 id={`${uid}-ingredients-heading`} className="ingredient-story__heading ingredient-story__heading--gradient">{story.heading[0]}<br />{story.heading[1]}</h2>
            </div>
            <p>{story.intro}</p>
          </div>
          <div ref={trackRef} className="ingredient-story__track">
            <div ref={stageRef} className="ingredient-story__stage">
              <div className="ingredient-story__grid">
                <figure ref={artRef} className="ingredient-story__art" aria-labelledby={captionId}>
                  {forms.map((form, index) => (
                    <img key={form.image} src={form.image} alt={index === activeIndex ? form.alt : ""} aria-hidden={index !== activeIndex} className={`ingredient-story__art-image${index === activeIndex ? " is-active" : ""}`} width={1254} height={1254} loading="lazy" decoding="async" />
                  ))}
                  <div className="ingredient-story__art-overline" aria-hidden="true"><span>TRUWE</span><span>PRECISION FORMULATED</span></div>
                  <figcaption id={captionId} className="ingredient-story__art-caption">{activeForm.caption}</figcaption>
                </figure>
                <div className="ingredient-story__selection">
                  <h3 className="ingredient-story__selection-heading">{story.selectionHeading}</h3>
                  <div className="ingredient-story__list-labels" aria-hidden="true"><span>INGREDIENT SPOTLIGHT</span><span>{story.serving.toUpperCase()}</span></div>
                  <div className="ingredient-story__tabs" role="tablist" aria-label={`Explore ${story.name} ingredients`} aria-orientation={horizontal ? "horizontal" : "vertical"}>
                    {forms.map((form, index) => (
                      <Button variant="ghost" key={form.image} ref={(element) => { tabRefs.current[index] = element; }} type="button" id={tabId(index)} role="tab" aria-controls={panelId} aria-selected={activeIndex === index} tabIndex={activeIndex === index ? 0 : -1} onClick={() => selectForm(index)} onKeyDown={(event) => onTabKeyDown(event, index)}>
                        <img className="ingredient-story__thumb" src={form.image} alt="" width={64} height={64} loading="lazy" decoding="async" />
                        <span className="ingredient-story__form-name">{form.shortName}<small>{form.subtitle}</small></span>
                        <span className="ingredient-story__amount">{form.dose}</span>
                        <ArrowIcon />
                      </Button>
                    ))}
                  </div>
                  <div id={panelId} className="ingredient-story__description" role="tabpanel" tabIndex={0} aria-labelledby={tabId(activeIndex)}>
                    <span className="ingredient-story__eyebrow">{activeForm.kicker} · {story.serving}</span>
                    <div className="ingredient-story__panel-heading"><h3>{activeForm.name}</h3><span className="ingredient-story__mobile-dose">{activeForm.dose}</span></div>
                    <p>{activeForm.copy}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="ingredient-story__art-note">Ingredient-inspired illustrations.</p>
        </div>
      </section>
      <section className="ingredient-story__cofactors ingredient-story__wrap" aria-labelledby={`${uid}-cofactors-heading`}>
        <div className="ingredient-story__cofactors-copy">
          <p className="ingredient-story__eyebrow">THE COMPLETE FORMULA</p>
          <h2 id={`${uid}-cofactors-heading`} className="ingredient-story__heading ingredient-story__heading--gradient">{product === "ova" ? <>More in every<br />daily sachet.</> : <>Five actives.<br />One daily serving.</>}</h2>
          <p>{product === "ova" ? "Zinc bisglycinate and chromium picolinate complement the five featured ingredients. All in the same daily sachet." : "The five featured actives are combined in a two-tablet daily serving. The formula also includes Bio-Absorb Matrix."}</p>
        </div>
        <div className="ingredient-story__cofactor-card">
          <p className="ingredient-story__eyebrow">{story.serving.toUpperCase()}</p>
          {story.otherIngredients.length > 0 && <dl>{story.otherIngredients.map((ingredient) => <div key={ingredient.name}><dt>{ingredient.name}</dt><dd>{ingredient.dose}</dd></div>)}</dl>}
          <div className="ingredient-story__matrix"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20 3C7 2 2 9 5 15s14 5 15-12ZM4 21 15 9" /></svg><div><strong>Bio-Absorb Matrix</strong><p>Proprietary blend.</p></div></div>
        </div>
      </section>
    </div>
  );
}
